import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getProductByReviewToken, 
  getReviewIteration, 
  createPin, 
  approveByVendor, 
  markIterationViewed 
} from '@/data/vendorReview';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { CheckCircle2, MapPin, MessageSquare } from 'lucide-react';

export default function VendorReview() {
  const { token } = useParams();
  const queryClient = useQueryClient();
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approverEmail, setApproverEmail] = useState('');
  const [isAddingPin, setIsAddingPin] = useState(false);
  const [pendingPin, setPendingPin] = useState<{ x: number; y: number } | null>(null);
  const [pinText, setPinText] = useState('');
  const [pinAuthorName, setPinAuthorName] = useState('');
  
  const { data: product, isLoading: loadingProduct } = useQuery({
    queryKey: ['vendorProduct', token],
    queryFn: () => getProductByReviewToken(token!),
    enabled: !!token,
  });
  
  const { data: reviewData, isLoading: loadingReview } = useQuery({
    queryKey: ['reviewIteration', product?.id],
    queryFn: () => getReviewIteration(product!.id),
    enabled: !!product,
  });
  
  useEffect(() => {
    if (product && reviewData?.currentIteration) {
      markIterationViewed(product.id, reviewData.currentIteration);
    }
  }, [product, reviewData?.currentIteration]);
  
  const approveMutation = useMutation({
    mutationFn: () => approveByVendor(product!.id, approverEmail),
    onSuccess: () => {
      toast.success('Product approved! It will go live shortly.');
      queryClient.invalidateQueries({ queryKey: ['vendorProduct', token] });
    },
    onError: (error: any) => {
      toast.error('Failed to approve: ' + error.message);
    },
  });
  
  const createPinMutation = useMutation({
    mutationFn: createPin,
    onSuccess: () => {
      toast.success('Comment added successfully');
      queryClient.invalidateQueries({ queryKey: ['reviewIteration', product?.id] });
      setPendingPin(null);
      setPinText('');
      setPinAuthorName('');
      setIsAddingPin(false);
    },
    onError: (error: any) => {
      toast.error('Failed to add comment: ' + error.message);
    },
  });
  
  const handleApprove = () => {
    if (!approverEmail) {
      toast.error('Please enter your email to confirm approval');
      return;
    }
    approveMutation.mutate();
    setShowApproveModal(false);
  };
  
  const handlePageClick = (e: React.MouseEvent) => {
    if (!isAddingPin || !product) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setPendingPin({ x, y });
  };
  
  const handleSubmitPin = () => {
    if (!pendingPin || !pinText || !product) return;
    
    createPinMutation.mutate({
      product_id: product.id,
      x_percent: pendingPin.x,
      y_percent: pendingPin.y,
      text: pinText,
      author_name: pinAuthorName,
      author_email: approverEmail,
    });
  };
  
  if (loadingProduct || loadingReview) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading product...</p>
        </div>
      </div>
    );
  }
  
  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 max-w-md text-center">
          <h1 className="text-2xl font-bold mb-2">Product Not Found</h1>
          <p className="text-muted-foreground">The review link you're using is invalid or expired.</p>
        </Card>
      </div>
    );
  }
  
  const pins = reviewData?.pins || [];
  const openPins = pins.filter(p => p.status === 'open');
  const resolvedPins = pins.filter(p => p.status === 'resolved');
  
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">{product.title}</h1>
              <p className="text-sm text-muted-foreground">
                Iteration #{reviewData?.currentIteration || 1} • 
                {isAddingPin ? ' Click anywhere to add a comment' : ' Review your product listing'}
              </p>
            </div>
            
            <div className="flex gap-3 items-center">
              <Button
                variant={isAddingPin ? 'destructive' : 'outline'}
                onClick={() => setIsAddingPin(!isAddingPin)}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                {isAddingPin ? 'Cancel' : 'Add Comment'}
              </Button>
              
              <Button
                size="lg"
                onClick={() => setShowApproveModal(true)}
                className="bg-green-600 hover:bg-green-700"
                disabled={openPins.length > 0}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Approve & Publish
              </Button>
            </div>
          </div>
          
          {openPins.length > 0 && (
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                <strong>{openPins.length}</strong> open comment{openPins.length !== 1 ? 's' : ''} to address before approval
              </p>
            </div>
          )}
        </div>
      </header>
      
      <div className="flex">
        <div 
          className="flex-1 p-8 relative cursor-crosshair"
          onClick={isAddingPin ? handlePageClick : undefined}
        >
          <div className="max-w-4xl mx-auto">
            <div className="bg-card rounded-lg p-8 border relative">
              {product.image && (
                <img src={product.image} alt={product.title} className="w-full h-64 object-cover rounded-lg mb-6" />
              )}
              
              <h2 className="text-3xl font-bold mb-4">{product.title}</h2>
              {product.subtitle && (
                <p className="text-xl text-muted-foreground mb-6">{product.subtitle}</p>
              )}
              
              {product.description && (
                <div className="prose dark:prose-invert max-w-none mb-8">
                  <p>{product.description}</p>
                </div>
              )}
              
              {/* Render pins on the content */}
              {pins.map((pin, index) => (
                <div
                  key={pin.id}
                  className="absolute"
                  style={{
                    left: `${pin.x_percent}%`,
                    top: `${pin.y_percent}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <div className="relative group">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg cursor-pointer ${
                      pin.status === 'resolved' 
                        ? 'bg-green-500 text-white' 
                        : 'bg-primary text-primary-foreground'
                    }`}>
                      {pin.status === 'resolved' ? (
                        <CheckCircle2 className="w-4 h-4" />
                      ) : (
                        <span className="text-xs font-bold">{index + 1}</span>
                      )}
                    </div>
                    
                    <div className="absolute left-full ml-2 top-0 hidden group-hover:block w-64 z-50">
                      <Card className="p-3 shadow-xl">
                        <div className="flex items-start justify-between mb-2">
                          <Badge variant={pin.status === 'resolved' ? 'default' : 'secondary'}>
                            {pin.status}
                          </Badge>
                          {pin.author_name && (
                            <span className="text-xs text-muted-foreground">{pin.author_name}</span>
                          )}
                        </div>
                        <p className="text-sm">{pin.text}</p>
                        {pin.resolution_notes && (
                          <div className="mt-2 pt-2 border-t">
                            <p className="text-xs text-muted-foreground">Resolution: {pin.resolution_notes}</p>
                          </div>
                        )}
                      </Card>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Pending pin marker */}
              {pendingPin && (
                <div
                  className="absolute w-8 h-8 rounded-full bg-red-500 flex items-center justify-center shadow-lg animate-pulse"
                  style={{
                    left: `${pendingPin.x}%`,
                    top: `${pendingPin.y}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <MapPin className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
          </div>
        </div>
        
        <aside className="w-96 border-l bg-card p-6 overflow-y-auto h-screen sticky top-0">
          <h3 className="text-lg font-semibold mb-4">Comments ({pins.length})</h3>
          
          <div className="space-y-4">
            {pins.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No comments yet. Click "Add Comment" to get started.
              </p>
            ) : (
              pins.map((pin, index) => (
                <Card key={pin.id} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                        pin.status === 'resolved' 
                          ? 'bg-green-500 text-white' 
                          : 'bg-primary text-primary-foreground'
                      }`}>
                        {pin.status === 'resolved' ? '✓' : index + 1}
                      </div>
                      <Badge variant={pin.status === 'resolved' ? 'default' : 'secondary'}>
                        {pin.status}
                      </Badge>
                    </div>
                    {pin.author_name && (
                      <span className="text-xs text-muted-foreground">{pin.author_name}</span>
                    )}
                  </div>
                  
                  <p className="text-sm mb-2">{pin.text}</p>
                  
                  {pin.resolution_notes && (
                    <div className="mt-2 pt-2 border-t">
                      <p className="text-xs text-muted-foreground">
                        <strong>Resolution:</strong> {pin.resolution_notes}
                      </p>
                    </div>
                  )}
                  
                  <p className="text-xs text-muted-foreground mt-2">
                    {new Date(pin.created_at).toLocaleDateString()}
                  </p>
                </Card>
              ))
            )}
          </div>
        </aside>
      </div>
      
      {/* Add Pin Dialog */}
      <Dialog open={!!pendingPin} onOpenChange={() => setPendingPin(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Comment</DialogTitle>
            <DialogDescription>
              Describe what you'd like changed or corrected at this location.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="pinText">Comment *</Label>
              <Input
                id="pinText"
                value={pinText}
                onChange={(e) => setPinText(e.target.value)}
                placeholder="e.g., Update this pricing to $99/month"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="pinAuthorName">Your Name (optional)</Label>
              <Input
                id="pinAuthorName"
                value={pinAuthorName}
                onChange={(e) => setPinAuthorName(e.target.value)}
                placeholder="e.g., John Doe"
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingPin(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitPin} 
              disabled={!pinText || createPinMutation.isPending}
            >
              {createPinMutation.isPending ? 'Adding...' : 'Add Comment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Approve Modal */}
      <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve & Publish Product</DialogTitle>
            <DialogDescription>
              By approving, your product listing will go live on Circle Network and be visible to thousands of real estate professionals.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="approverEmail">Confirm Your Email *</Label>
              <Input
                id="approverEmail"
                type="email"
                value={approverEmail}
                onChange={(e) => setApproverEmail(e.target.value)}
                placeholder="your@email.com"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                We'll send you a confirmation once your product is live.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleApprove} 
              disabled={!approverEmail || approveMutation.isPending}
              className="bg-green-600 hover:bg-green-700"
            >
              {approveMutation.isPending ? 'Publishing...' : 'Approve & Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
